import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";

const images = [
  "/pdf/1.jpg", "/pdf/2.jpg", "/pdf/3.jpg", "/pdf/4.jpg", "/pdf/5.jpg",
  "/pdf/6.jpg", "/pdf/7.jpg", "/pdf/8.jpg", "/pdf/9.jpg", "/pdf/10.jpg",
  "/pdf/11.jpg", "/pdf/12.jpg", "/pdf/13.jpg", "/pdf/14.jpg", "/pdf/15.jpg",
  "/pdf/16.jpg", "/pdf/17.jpg", "/pdf/18.jpg", "/pdf/19.jpg", "/pdf/20.jpg",
];

const CompanyProfile = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>Company Profile | Sooners Land LLC</title>
        <meta name="description" content="Detailed company profile and service offerings of Sooners Land LLC Engineering." />
      </Helmet>

       {/* Header */}
       <section className="bg-primary pt-32 pb-16 px-6 md:px-12 text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-accent/5 skew-x-12 translate-x-1/2" />
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-accent-soft rounded-2xl mb-6"
          >
            <FileText className="text-accent" size={32} />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">Company Profile</h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Explore our comprehensive capabilities and strategic overview.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16 flex flex-col items-center -mt-10">
        
        {/* Gallery Viewer */}
        <div className="relative group w-full max-w-5xl">
          <div className="glass-card bg-white p-4 md:p-8 rounded-[2.5rem] shadow-2xl border border-slate-200 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentIndex}
                src={images[currentIndex]}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                alt={`Profile Page ${currentIndex + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
              />
            </AnimatePresence>

            {/* Navigation Controls */}
            <div className="absolute inset-y-0 left-4 md:left-10 flex items-center">
              <button
                onClick={goToPrevious}
                className="w-12 h-12 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-primary hover:bg-accent hover:text-white transition-all duration-300 -translate-x-6 md:translate-x-0 group-hover:translate-x-0 opacity-0 group-hover:opacity-100"
              >
                <ChevronLeft size={24} />
              </button>
            </div>
            <div className="absolute inset-y-0 right-4 md:right-10 flex items-center">
              <button
                onClick={goToNext}
                className="w-12 h-12 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-primary hover:bg-accent hover:text-white transition-all duration-300 translate-x-6 md:translate-x-0 group-hover:translate-x-0 opacity-0 group-hover:opacity-100"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>

          {/* Page Indicator */}
          <div className="mt-8 flex items-center justify-between bg-white px-8 py-4 rounded-2xl shadow-sm border border-slate-100">
             <div className="flex items-center gap-2">
                <span className="text-xl font-black text-accent">{currentIndex + 1}</span>
                <span className="text-slate-300 text-sm font-bold uppercase tracking-widest">/ {images.length} Pages</span>
             </div>
             
             {/* Thumbnail dots */}
             <div className="hidden md:flex gap-1.5">
                {images.map((_, i) => (
                  <div 
                    key={i} 
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === currentIndex ? 'w-6 bg-accent' : 'w-1.5 bg-slate-200'}`}
                  />
                ))}
             </div>

             <div className="flex gap-2">
                <button onClick={goToPrevious} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={goToNext} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary transition-colors">
                  <ChevronRight size={20} />
                </button>
             </div>
          </div>
        </div>

        {/* Download Section */}
        <div className="mt-16 text-center">
          <a
            href="/SoonersLand.pdf"
            download="CompanyProfile.pdf"
            className="btn-primary inline-flex gap-3 px-10 py-5 rounded-3xl text-xl shadow-2xl shadow-emerald-500/10 bg-emerald-600 hover:bg-emerald-700 !border-0"
          >
            <Download size={24} />
            Download Technical Profile (PDF)
          </a>
          <p className="mt-4 text-sm text-slate-400 font-medium italic">
            Full document includes detailed project case studies and technical specifications.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;